import React, { useState, useRef, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import * as tus from "tus-js-client";
// FFmpeg 0.12+ WORKERFS mount lets us read directly from a File/Blob without
// copying it into WASM memory. Critical for recordings >4 GB — WASM32 has a
// hard 4 GB address-space cap, so any writeFile() with a bigger source fails
// with "File could not be read! Code=-1". Mounting with WORKERFS bypasses this
// entirely: FFmpeg reads bytes on-demand from the underlying File object.
import {
  Video, Upload, Film, Zap, AlertTriangle, CheckCircle2,
  Loader2, PlayCircle, Star, Lock, Rocket, Info
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ReplayTabs from "@/components/ReplayTabs";

// -----------------------------------------------------------------------------
// Video Trade Replay — Pro-tier only
//
// Flow:
// 1. User selects an OBS `.mp4` recording
// 2. We read metadata + file lastModified to compute recording start time
// 3. Fetch the user's trades for that day
// 4. For each trade, compute [entry_time - buffer, exit_time + buffer] in video time
// 5. Client-side FFmpeg.wasm cuts each segment into an .mp4
// 6. Each clip uploaded to /api/video/clips, associated with the trade
// -----------------------------------------------------------------------------

// FFmpeg core is loaded once and reused across cuts.
const ffmpeg = new FFmpeg();
let ffmpegLoadedPromise = null;
const loadFfmpeg = async (onLog) => {
  if (ffmpegLoadedPromise) return ffmpegLoadedPromise;
  ffmpegLoadedPromise = (async () => {
    // Use CDN-hosted core. If offline / blocked, this promise rejects and we throw a friendly error.
    const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd";
    ffmpeg.on("log", ({ message }) => onLog && onLog(message));
    await ffmpeg.load({
      coreURL: `${baseURL}/ffmpeg-core.js`,
      wasmURL: `${baseURL}/ffmpeg-core.wasm`,
    });
    return ffmpeg;
  })();
  return ffmpegLoadedPromise;
};

const formatBytes = (b) => {
  if (!b) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let n = b;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
};

const fmtUSD = (n) => {
  const v = Number(n) || 0;
  return `${v >= 0 ? "+" : "-"}$${Math.abs(v).toFixed(2)}`;
};

// Probe a video file to get its duration + creation time.
// Uses HTMLVideoElement metadata (works on all major browsers, no ffmpeg roundtrip).
const probeVideo = (file) => new Promise((resolve, reject) => {
  const video = document.createElement("video");
  video.preload = "metadata";
  video.onloadedmetadata = () => {
    URL.revokeObjectURL(video.src);
    resolve({ duration: video.duration || 0 });
  };
  video.onerror = () => { URL.revokeObjectURL(video.src); reject(new Error("Could not probe video")); };
  video.src = URL.createObjectURL(file);
});

// Retry helper for chunked/single-shot uploads. Retries on transient 5xx or
// network errors (Cloudflare 520/502/504, connection reset) with exponential
// backoff. Does NOT retry 4xx errors (auth failure, bad payload) — those
// won't get better on retry.
const uploadWithRetry = async (fn, attempts = 3) => {
  let lastErr = null;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // 4xx (except 408 timeout, 429 rate-limit) is a permanent failure —
      // don't waste time retrying.
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw err;
      }
      // Exponential backoff: 1.5s → 3s → 6s
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, 1500 * Math.pow(2, i)));
      }
    }
  }
  throw lastErr;
};

// Compute recording start time from file. Strategy:
//   1. Try to parse from OBS-style filename (e.g. "2026-07-05 09-28-15.mp4")
//   2. Fall back to file.lastModified - duration (OBS writes lastModified at recording end)
// We ALWAYS probe duration too — the trades-in-range filter needs both start AND end.
//
// Duration reliability: Chrome's HTMLVideoElement.duration lies for very large
// MP4s when the `moov` atom is at the end of the file (OBS default). It can
// return Infinity, NaN, or a truncated value based on metadata-only bytes.
// We cross-check against:
//   (a) `file.lastModified - startAt` — usually the real recording length,
//       assuming Windows didn't touch the file after OBS closed it.
//   (b) A file-size ÷ typical-bitrate estimate — for OBS the record bitrate is
//       ~6-8 Mbps; a 7 GB file at 8 Mbps = 7 * 8 * 1024 / 8 = 7168s ≈ 2h. We
//       use a CONSERVATIVE 16 Mbps (2 MB/s) upper-bound so the ESTIMATE is a
//       LOWER-BOUND on duration. If the probed duration is significantly
//       shorter than this floor, the probe is wrong and we use the estimate.
//
// We take the MAX of all reliable signals so trades near the end of long
// recordings are never dropped by a truncated metadata probe.
const ESTIMATED_MAX_BITRATE_BPS = 16 * 1024 * 1024 / 8;  // 16 Mbps → 2 MB/s

const inferRecordingStart = async (file) => {
  // Probe duration up-front — needed by both strategies to compute the recording END.
  let probedDuration = null;
  try {
    const probed = await probeVideo(file);
    // Reject Infinity / NaN / <1s — these all mean Chrome couldn't decode the metadata.
    const d = Number(probed?.duration);
    if (Number.isFinite(d) && d > 1) probedDuration = d;
  } catch (_e) { /* ignore — filename may still give us the start, and we'll fall back to lastModified span for duration */ }

  // File-size-based lower bound: if the file is B bytes and OBS records at
  // AT MOST 16 Mbps (a generous ceiling), the recording is AT LEAST B/2MB/s
  // seconds long. Use this as a floor on duration.
  const sizeBasedFloor = file.size ? Math.floor(file.size / ESTIMATED_MAX_BITRATE_BPS) : 0;

  // Strategy 1: filename regex
  const nameMatch = file.name.match(/(\d{4})-(\d{2})-(\d{2})[\s_]*(\d{2})-(\d{2})-(\d{2})/);
  if (nameMatch) {
    const [_, y, mo, d, h, mi, s] = nameMatch;
    const dt = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}`);  // parsed in LOCAL tz — matches how OBS names files
    if (!isNaN(dt.getTime())) {
      // Ground-truth duration = time between start (from filename) and file
      // close (lastModified).
      const spanSec = file.lastModified ? Math.max(0, (file.lastModified - dt.getTime()) / 1000) : 0;
      // Take the MAX of everything we know so a truncated metadata probe
      // never wins.
      const duration = Math.max(probedDuration || 0, spanSec, sizeBasedFloor) || null;
      // Track how we got here so we can show a debug hint in the UI.
      const durationSources = [];
      if (probedDuration) durationSources.push({ src: "metadata", sec: probedDuration });
      if (spanSec) durationSources.push({ src: "file-modified-span", sec: spanSec });
      if (sizeBasedFloor) durationSources.push({ src: "size÷bitrate-floor", sec: sizeBasedFloor });
      return { source: "filename", startAt: dt, duration, durationSources };
    }
  }
  // Strategy 2: lastModified minus duration (or size floor if probe failed)
  const endMs = file.lastModified;
  const durationForBackCalc = probedDuration || sizeBasedFloor;
  if (endMs && durationForBackCalc) {
    return {
      source: "lastModified",
      startAt: new Date(endMs - durationForBackCalc * 1000),
      duration: durationForBackCalc,
      durationSources: probedDuration
        ? [{ src: "metadata", sec: probedDuration }]
        : [{ src: "size÷bitrate-floor", sec: sizeBasedFloor }],
    };
  }
  return { source: "unknown", startAt: null, duration: probedDuration, durationSources: [] };
};

// Interpret a naive ISO string (no Z, no +HH:MM) as wall-clock time in `tz` and
// return the absolute UTC ms. Necessary because most brokers export trade times
// in market timezone (ET) without a timezone marker — `new Date(...)` would then
// wrongly parse them as the browser's local timezone, off by hours.
const parseNaiveInTz = (iso, tz) => {
  if (!iso) return NaN;
  // Already timezone-tagged? trust the Date parser.
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(iso)) return new Date(iso).getTime();
  if (!tz) return new Date(iso).getTime();  // no tz configured → old behavior
  // Trick: parse the string as if it were UTC, then compute how many ms `tz`
  // is offset from UTC at that instant, and subtract.
  const asIfUtc = new Date(iso + "Z").getTime();
  if (isNaN(asIfUtc)) return NaN;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    }).formatToParts(new Date(asIfUtc));
    const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    const wallInUtc = Date.UTC(
      Number(m.year), Number(m.month) - 1, Number(m.day),
      Number(m.hour) % 24, Number(m.minute), Number(m.second)
    );
    const offsetMs = wallInUtc - asIfUtc;  // tz's offset from UTC (negative = behind UTC)
    return asIfUtc - offsetMs;
  } catch {
    return new Date(iso).getTime();
  }
};

export default function ReplayPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });

  const isPro = billing?.plan_tier === "pro";

  const { data: quota } = useQuery({
    queryKey: ["video-quota"],
    queryFn: async () => (await api.get("/video/storage-quota")).data,
    enabled: isPro,
  });

  const [file, setFile] = useState(null);
  const [inferred, setInferred] = useState(null);
  const [manualStart, setManualStart] = useState("");  // ISO "yyyy-mm-ddThh:mm:ss" for override
  const [manualEnd, setManualEnd] = useState("");      // Optional end-of-recording override
  const [bufferSeconds, setBufferSeconds] = useState(5);
  const [excludedTradeIds, setExcludedTradeIds] = useState(() => new Set());
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, label: "" });
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [ffmpegProgress, setFfmpegProgress] = useState("");
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const inputRef = useRef(null);

  // Pick file & auto-infer start
  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    setInferred(null);
    setManualStart("");
    try {
      const info = await inferRecordingStart(f);
      setInferred(info);
      if (info.startAt) {
        // Prefill manual input for override
        const pad = (n) => String(n).padStart(2, "0");
        const d = info.startAt;
        setManualStart(
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
        );
      }
    } catch (e) {
      toast.error("Could not read video metadata: " + e.message);
    }
  };

  const startAtDate = useMemo(() => {
    if (manualStart) {
      const d = new Date(manualStart);
      if (!isNaN(d.getTime())) return d;
    }
    return inferred?.startAt || null;
  }, [manualStart, inferred]);

  // Recording END time — user override wins, else startAt + duration, else
  // fall back to market close (4:15 PM ET) OR end-of-local-day, whichever is
  // later. This mirrors the start override so users whose browser mis-probes
  // duration can just type the true stop time — but for the common case
  // (morning trading session recording), the market-close fallback saves them
  // from having to override at all.
  const endAtDate = useMemo(() => {
    if (manualEnd) {
      const d = new Date(manualEnd);
      if (!isNaN(d.getTime())) return d;
    }
    if (!startAtDate) return null;
    const startMs = startAtDate.getTime();

    // Candidate 1: startAt + probed/estimated duration
    const durEnd = inferred?.duration ? new Date(startMs + inferred.duration * 1000) : null;

    // Candidate 2: market close (4:15 PM ET) on the same trading day.
    // We interpret "same trading day" as the local YYYY-MM-DD of the start.
    // Only apply this fallback when the computed duration looks SHORT compared
    // to a full trading session (<6h) — otherwise trust the file.
    let marketCloseEnd = null;
    try {
      const pad = (n) => String(n).padStart(2, "0");
      const yyyy = startAtDate.getFullYear();
      const mm = pad(startAtDate.getMonth() + 1);
      const dd = pad(startAtDate.getDate());
      // "4:15 PM ET" — build the wall-clock instant in America/New_York and
      // convert to absolute UTC ms via our helper.
      const marketCloseMs = parseNaiveInTz(`${yyyy}-${mm}-${dd}T16:15:00`, "America/New_York");
      if (!isNaN(marketCloseMs) && marketCloseMs > startMs) {
        marketCloseEnd = new Date(marketCloseMs);
      }
    } catch { /* ignore */ }

    // Suspiciously short = probed duration < 6h. If so, extend to market close.
    const suspiciouslyShort = !inferred?.duration || inferred.duration < 6 * 3600;
    if (suspiciouslyShort && marketCloseEnd) {
      return durEnd && durEnd > marketCloseEnd ? durEnd : marketCloseEnd;
    }
    if (durEnd) return durEnd;

    // Last-resort fallback: end of the same local day.
    const eod = new Date(startAtDate);
    eod.setHours(23, 59, 59, 999);
    return eod;
  }, [manualEnd, startAtDate, inferred]);

  // Once we have the start date, figure out which YYYY-MM-DD (in the trader's LOCAL tz)
  const targetDate = useMemo(() => {
    if (!startAtDate) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return `${startAtDate.getFullYear()}-${pad(startAtDate.getMonth() + 1)}-${pad(startAtDate.getDate())}`;
  }, [startAtDate]);

  // Load that day's trades
  const { data: trades } = useQuery({
    queryKey: ["trades-for-date", targetDate],
    queryFn: async () => {
      if (!targetDate) return [];
      const r = await api.get("/trades", { params: { from: targetDate, to: targetDate } });
      return r.data || [];
    },
    enabled: !!targetDate && isPro,
  });

  // The user's broker timezone (where trades are exported from) and local
  // timezone. Trade timestamps in the DB are usually naive ISO strings in the
  // broker's TZ — we need to interpret them in that TZ to align correctly.
  const brokerTz = user?.broker_timezone || "America/New_York";
  const userTz = user?.timezone || (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null);

  // ALL trades on this local date, annotated with whether each falls inside the
  // recording window. We show all of them (grouped/sorted by entry time) so the
  // user can see the full picture; ones outside the window are grayed out and
  // excluded from cutting unless the user overrides.
  const tradesOnDay = useMemo(() => {
    if (!trades || !startAtDate || !endAtDate) return [];
    const pad = (n) => String(n).padStart(2, "0");
    const dayStr = `${startAtDate.getFullYear()}-${pad(startAtDate.getMonth() + 1)}-${pad(startAtDate.getDate())}`;
    const startMs = startAtDate.getTime();
    const endMs = endAtDate.getTime();
    const bufMs = bufferSeconds * 1000;
    return (trades || [])
      .map((t) => ({
        ...t,
        // Compute the ABSOLUTE UTC ms once — interpreting the naive broker-TZ
        // string correctly — and cache them for filtering & clip math below.
        __et: parseNaiveInTz(t.entry_time, brokerTz),
        __xt: parseNaiveInTz(t.exit_time, brokerTz),
      }))
      .filter((t) => {
        if (isNaN(t.__et)) return false;
        // Filter by "same LOCAL day as the recording" using the user's tz view.
        const et = new Date(t.__et);
        const y = et.getFullYear(), m = pad(et.getMonth() + 1), d = pad(et.getDate());
        return `${y}-${m}-${d}` === dayStr;
      })
      .map((t) => {
        const inWindow = t.__et < endMs + bufMs && t.__xt > startMs - bufMs;
        return { ...t, __inWindow: inWindow };
      })
      .sort((a, b) => a.__et - b.__et);
  }, [trades, startAtDate, endAtDate, bufferSeconds, brokerTz]);

  // Everything the user hasn't manually unchecked, restricted to trades that
  // are in-window OR that the user explicitly kept (via the "cut anyway" toggle).
  const tradesToCut = useMemo(
    () => tradesOnDay.filter((t) => t.__inWindow && !excludedTradeIds.has(t.id)),
    [tradesOnDay, excludedTradeIds]
  );

  const process = async () => {
    if (!file || !startAtDate || !tradesToCut.length) return;
    setProcessing(true);
    setProgress({ done: 0, total: tradesToCut.length, label: "Loading FFmpeg…" });
    try {
      await loadFfmpeg((msg) => setFfmpegProgress(msg));
      setFfmpegLoaded(true);

      // Create upload session
      const session = await api.post("/video/upload-session", {
        source_file_name: file.name,
        recording_start_at: startAtDate.toISOString(),
        buffer_seconds: bufferSeconds,
        total_clips: tradesToCut.length,
      });
      const uploadSessionId = session.data.id;

      // Mount the source file into FFmpeg's virtual FS via WORKERFS instead of
      // copying its bytes with writeFile(). This means FFmpeg reads directly
      // from the underlying File object as it needs each byte range — so we
      // support recordings of ANY size (multi-hour OBS captures included).
      setProgress((p) => ({ ...p, label: "Mounting recording…" }));
      const MOUNT_DIR = "/mnt";
      try { await ffmpeg.createDir(MOUNT_DIR); } catch { /* already exists */ }
      // Some environments mount over an existing mount; unmount defensively.
      try { await ffmpeg.unmount(MOUNT_DIR); } catch { /* not mounted */ }
      await ffmpeg.mount("WORKERFS", { files: [file] }, MOUNT_DIR);
      const inputPath = `${MOUNT_DIR}/${file.name}`;

      // For each trade we need a duration to clamp against — prefer probed,
      // fall back to endAt-startAt, else a huge sentinel so we never clamp.
      const recDurationS = inferred?.duration
        ? inferred.duration
        : endAtDate ? (endAtDate.getTime() - startAtDate.getTime()) / 1000
        : Number.MAX_SAFE_INTEGER;

      const startMs = startAtDate.getTime();

      // Fetch existing clips for the target date and filter out trades that
      // already have a clip. Prevents duplicates on retry after a partial
      // failure — the user can safely click "Process" again without worrying
      // about re-uploading trades that already went through.
      let toProcess = tradesToCut;
      try {
        if (targetDate) {
          const existingR = await api.get("/video/clips", { params: { date: targetDate } });
          const existingTradeIds = new Set((existingR.data || []).map((c) => c.trade_id).filter(Boolean));
          const skipped = tradesToCut.filter((t) => existingTradeIds.has(t.id));
          toProcess = tradesToCut.filter((t) => !existingTradeIds.has(t.id));
          if (skipped.length > 0) {
            toast(`Skipping ${skipped.length} trade${skipped.length === 1 ? "" : "s"} that already have a clip.`, { icon: "↻" });
          }
        }
      } catch (e) {
        // Non-fatal — if this fetch fails we just process everything (server
        // dedupe will still catch true duplicates).
        console.warn("Existing-clip pre-check failed:", e);
      }
      if (!toProcess.length) {
        setProgress({ done: 0, total: 0, label: "Nothing to do — every trade already has a clip." });
        setProcessing(false);
        toast.success("All trades in this window already have clips.");
        return;
      }

      for (let i = 0; i < toProcess.length; i++) {
        const t = toProcess[i];
        // Use pre-parsed absolute UTC ms (broker-TZ aware) — see tradesOnDay above.
        const et = t.__et;
        const xt = t.__xt;
        const clipStartS = Math.max(0, (et - startMs) / 1000 - bufferSeconds);
        const clipEndS = Math.min(recDurationS, (xt - startMs) / 1000 + bufferSeconds);
        const clipDur = Math.max(1, clipEndS - clipStartS);

        setProgress({
          done: i,
          total: toProcess.length,
          label: `Cutting clip ${i + 1}/${toProcess.length} · ${t.symbol}`,
        });

        const outName = `clip_${i}.mp4`;
        // -ss BEFORE -i seeks by keyframe (fast, no decode). Stream copy = no
        // re-encode. Works for any file size because input is streamed from
        // the mount, not loaded into memory.
        await ffmpeg.exec([
          "-ss", String(clipStartS),
          "-i", inputPath,
          "-t", String(clipDur),
          "-c", "copy",
          "-avoid_negative_ts", "make_zero",
          outName,
        ]);

        const clipData = await ffmpeg.readFile(outName);
        const clipBlob = new Blob([clipData.buffer], { type: "video/mp4" });

        // Compute fill markers (multi-leg trades) — for MVP we just have entry+exit
        const fillMarkers = [];
        const entryOffset = (et - startMs) / 1000 - clipStartS;
        const exitOffset = (xt - startMs) / 1000 - clipStartS;
        if (entryOffset >= 0 && entryOffset <= clipDur) {
          fillMarkers.push({ time: entryOffset, price: t.entry_price, qty: t.quantity, kind: "entry" });
        }
        if (exitOffset >= 0 && exitOffset <= clipDur) {
          fillMarkers.push({ time: exitOffset, price: t.exit_price, qty: t.quantity, kind: "exit" });
        }

        // Upload — use chunked upload for files > 90 MB to bypass Cloudflare's
        // 100 MB request-body cap. Anything smaller uses the classic single-
        // shot multipart endpoint (fewer round-trips + a battle-tested code
        // path). Threshold raised from 40 → 90 MB after a customer reported
        // 404s on ~2-min clips (~60-80 MB) — those should use single-shot.
        setProgress({ done: i, total: toProcess.length, label: `Uploading clip ${i + 1}/${toProcess.length} · ${t.symbol}` });

        // ────────────────────────────────────────────────────────────────
        // DIRECT-TO-BUNNY UPLOAD via tus-js-client.
        //
        // The clip Blob goes straight from the browser to Bunny's CDN. Our
        // backend is not in the file-transfer path — it just mints the tus
        // session (`/video/clips/bunny/tus-session`) and later registers the
        // finished upload as a clip row (`/video/clips/bunny/register`).
        //
        // Why not go through our backend?
        //   • /tmp on ephemeral Cloud Run pods gets wiped on restarts, HPA
        //     scale-ups, and multi-pod routing → 404 on chunk N.
        //   • tus is genuinely resumable — if WiFi drops we pick up exactly
        //     where we left off (tus stores the state in localStorage).
        //   • Bypasses Cloudflare's 100 MB request cap.
        // ────────────────────────────────────────────────────────────────
        const tusSession = await uploadWithRetry(() => api.post("/video/clips/bunny/tus-session", {
          filename: outName,
          trade_id: t.id,
        }));
        const { endpoint, video_id, library_id, authorization_signature, authorization_expire } = tusSession.data;

        await new Promise((resolve, reject) => {
          const upload = new tus.Upload(clipBlob, {
            endpoint,
            retryDelays: [0, 3000, 6000, 12000, 24000],
            chunkSize: 5 * 1024 * 1024,       // 5 MB — Bunny's recommended default
            parallelUploads: 1,               // sequential; Bunny doesn't support parallel with a single video id
            headers: {
              AuthorizationSignature: authorization_signature,
              AuthorizationExpire: String(authorization_expire),
              VideoId: video_id,
              LibraryId: String(library_id),
            },
            metadata: {
              filetype: "video/mp4",
              title: outName,
            },
            onError: (err) => reject(err),
            onProgress: (bytesSent, bytesTotal) => {
              const pct = bytesTotal ? Math.round((bytesSent / bytesTotal) * 100) : 0;
              setProgress({ done: i, total: toProcess.length, label: `Uploading clip ${i + 1}/${toProcess.length} · ${t.symbol} · ${pct}%` });
            },
            onSuccess: () => resolve(),
          });
          upload.start();
        });

        // Upload done — register the clip in our DB so it shows up in the Library.
        await uploadWithRetry(() => api.post("/video/clips/bunny/register", {
          bunny_video_id: video_id,
          trade_id: t.id,
          date: targetDate,
          symbol: t.symbol,
          pnl: t.pnl,
          side: t.side,
          entry_time: t.entry_time,
          exit_time: t.exit_time,
          duration_seconds: clipDur,
          fill_markers: fillMarkers,
          upload_session_id: uploadSessionId,
          filename: outName,
          file_size: clipBlob.size,
        }));

        // Clean up ffmpeg FS
        try { await ffmpeg.deleteFile(outName); } catch { /* ignore */ }
      }

      try { await ffmpeg.unmount(MOUNT_DIR); } catch { /* ignore */ }

      setProgress({ done: toProcess.length, total: toProcess.length, label: "Done!" });
      qc.invalidateQueries({ queryKey: ["video-quota"] });
      qc.invalidateQueries({ queryKey: ["video-clips"] });
      toast.success(`Cut ${toProcess.length} clip${toProcess.length === 1 ? "" : "s"}. Check your Calendar or Trades to review.`);
      setFile(null);
      setInferred(null);
    } catch (e) {
      // Log the full error to console AND surface the useful bits in the toast.
      // Includes: HTTP status, the URL that failed, and any backend detail
      // string. Way easier to diagnose than the previous "Processing failed: e".
      console.error("Upload/processing failed:", e);
      const status = e?.response?.status;
      const url = e?.config?.url || e?.request?.responseURL || "";
      const detail = e?.response?.data?.detail || e?.message || String(e);
      const bits = [
        status ? `HTTP ${status}` : null,
        url ? `at ${url}` : null,
        detail,
      ].filter(Boolean).join(" · ");
      toast.error(`Processing failed: ${bits}`, { duration: 12000 });
    } finally {
      setProcessing(false);
    }
  };

  if (!billing) {
    return <div className="p-8 text-sm" style={{ color: "var(--fg-3)" }}>Loading…</div>;
  }

  // Gate: non-Pro users see upgrade prompt
  if (!isPro) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Video className="h-5 w-5" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
          <div className="label-eyebrow" style={{ color: "var(--accent)" }}>VIDEO TRADE REPLAY · PRO</div>
        </div>
        <h1 className="font-heading text-4xl font-semibold tracking-tight mb-4">
          Study your tape like <span style={{ color: "var(--accent)" }}>game film</span>.
        </h1>
        <p className="text-base leading-relaxed mb-8" style={{ color: "var(--fg-2)" }}>
          Upload your OBS screen recording. TradeGreen automatically cuts a video clip for every single
          trade — the seconds before your entry, the fill, the manage, and the exit. Each clip is
          attached to its trade so you can flip through wins/losses at the speed of thought.
          Nobody else does this.
        </p>

        <div
          className="p-8"
          style={{
            background: "linear-gradient(180deg, rgba(168,85,247,0.06), var(--surface) 60%)",
            border: "1px solid rgba(168,85,247,0.35)",
            borderRadius: "16px",
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <Lock className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <div className="label-eyebrow" style={{ color: "var(--accent)" }}>PRO-TIER FEATURE</div>
          </div>
          <h3 className="font-heading text-2xl font-semibold mb-2">Upgrade to unlock Video Replay</h3>
          <p className="text-sm mb-6" style={{ color: "var(--fg-2)" }}>
            Video Replay is included in <strong style={{ color: "var(--fg)" }}>TradeGreen Pro</strong> — plus
            unlimited AI Coach messages and every future feature first. $29.99/month, cancel anytime.
          </p>
          <button
            onClick={() => navigate("/pricing")}
            data-testid="upgrade-cta"
            className="px-5 py-3 text-sm font-semibold tracking-wide flex items-center gap-2"
            style={{
              background: "var(--accent)", color: "white", borderRadius: "10px",
              boxShadow: "0 4px 24px rgba(168,85,247,0.4)",
            }}
          >
            <Rocket className="h-4 w-4" strokeWidth={2} /> UPGRADE TO PRO
          </button>
        </div>
      </div>
    );
  }

  // Pro-tier UI
  return (
    <div>
      <ReplayTabs />
      <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <Video className="h-5 w-5" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <div className="label-eyebrow" style={{ color: "var(--accent)" }}>VIDEO TRADE REPLAY</div>
      </div>
      <div className="flex items-start justify-between mb-8 gap-4">
        <div className="flex-1">
          <h1 className="font-heading text-4xl font-semibold tracking-tight mb-2">Upload &amp; auto-cut your session</h1>
          <p className="text-sm" style={{ color: "var(--fg-2)" }}>
            Drop your OBS recording below. We&apos;ll detect the start time automatically and cut a clip for every trade you logged that day.
          </p>
        </div>
        <button
          onClick={() => setShowSetupGuide(true)}
          data-testid="obs-setup-btn"
          className="shrink-0 px-3 py-2 text-xs font-semibold tracking-wide flex items-center gap-1.5"
          style={{ background: "var(--surface)", color: "var(--fg-2)", border: "1px solid var(--border)", borderRadius: "8px" }}
        >
          <Info className="h-3.5 w-3.5" strokeWidth={1.5} /> OBS SETUP GUIDE
        </button>
      </div>

      {/* Storage quota */}
      {quota && (
        <div className="mb-6 p-4 flex items-center gap-4" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}>
          <Film className="h-5 w-5 shrink-0" strokeWidth={1.5} style={{ color: "var(--fg-3)" }} />
          <div className="flex-1">
            <div className="text-xs label-eyebrow" style={{ color: "var(--fg-3)" }}>STORAGE</div>
            <div className="mt-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--bg)" }}>
              <div
                className="h-full transition-all"
                style={{
                  width: `${Math.min(100, (quota.used_bytes / quota.hard_limit_bytes) * 100)}%`,
                  background: quota.over_hard ? "var(--loss)" : quota.over_soft ? "#f59e0b" : "var(--accent)",
                }}
              />
            </div>
          </div>
          <div className="text-xs font-mono-num text-right" style={{ color: "var(--fg-2)" }}>
            <span style={{ color: "var(--fg)" }}>{formatBytes(quota.used_bytes)}</span>
            <span style={{ color: "var(--fg-3)" }}> / {formatBytes(quota.hard_limit_bytes)}</span>
            <div className="text-[10px] mt-0.5" style={{ color: "var(--fg-3)" }}>{quota.clip_count} clips · {quota.retention_days}d retention</div>
          </div>
        </div>
      )}

      {/* Timezone alignment — critical for correct clip timing.
          If the user hasn't saved a timezone yet, we highlight this card so
          they set it BEFORE cutting clips. Otherwise it stays compact but
          still visible for reference/edits. */}
      <TimezoneCard
        currentTz={user?.timezone}
        currentBrokerTz={user?.broker_timezone}
      />

      {/* File picker */}
      <div
        className="p-8 text-center cursor-pointer transition-colors"
        style={{
          background: file ? "rgba(168,85,247,0.05)" : "var(--surface)",
          border: `2px dashed ${file ? "rgba(168,85,247,0.5)" : "var(--border)"}`,
          borderRadius: "16px",
        }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
        data-testid="video-drop-zone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/x-matroska"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="video-file-input"
        />
        {!file ? (
          <>
            <Upload className="h-8 w-8 mx-auto mb-3" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <div className="font-heading text-lg font-semibold">Drop your OBS recording here</div>
            <div className="text-xs mt-1" style={{ color: "var(--fg-3)" }}>MP4, MOV, or MKV · processed locally in your browser · no upload size limit</div>
          </>
        ) : (
          <>
            <CheckCircle2 className="h-8 w-8 mx-auto mb-3" strokeWidth={1.5} style={{ color: "var(--green)" }} />
            <div className="font-heading text-lg font-semibold break-all">{file.name}</div>
            <div className="text-xs mt-1 font-mono-num" style={{ color: "var(--fg-3)" }}>{formatBytes(file.size)}{inferred?.duration ? ` · ${Math.round(inferred.duration / 60)}m ${Math.round(inferred.duration % 60)}s` : ""}</div>
          </>
        )}
      </div>

      {/* Detected start + override */}
      {file && inferred && (
        <div className="mt-6 p-5 space-y-4" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}>
          <div className="flex items-start gap-3">
            <Info className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <div className="flex-1">
              <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>DETECTED RECORDING START</div>
              <div className="mt-1 text-sm font-mono-num" style={{ color: "var(--fg)" }}>
                {inferred.startAt
                  ? inferred.startAt.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "medium" })
                  : "Unknown — please enter manually"}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--fg-3)" }}>
                Source: {inferred.source === "filename" ? "OBS filename timestamp" : inferred.source === "lastModified" ? "file modification time − duration" : "unknown"}
                {inferred.duration && inferred.startAt ? (
                  <span data-testid="recording-duration">
                    {" · "}Duration: <span style={{ color: "var(--fg-2)" }}>
                      {Math.floor(inferred.duration / 3600)}h {Math.floor((inferred.duration % 3600) / 60)}m
                    </span>
                    {" · Ends "}
                    {new Date(inferred.startAt.getTime() + inferred.duration * 1000)
                      .toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2" style={{ color: "var(--fg-3)" }}>OVERRIDE START TIME</label>
            <input
              type="datetime-local"
              step="1"
              value={manualStart}
              onChange={(e) => setManualStart(e.target.value)}
              data-testid="recording-start-override"
              className="px-3 py-2 text-sm font-mono-num focus:outline-none"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--fg)", borderRadius: "8px" }}
            />
          </div>

          <div>
            <label className="label-eyebrow block mb-2" style={{ color: "var(--fg-3)" }}>OVERRIDE END TIME · when you clicked STOP in OBS (optional)</label>
            <input
              type="datetime-local"
              step="1"
              value={manualEnd}
              onChange={(e) => setManualEnd(e.target.value)}
              data-testid="recording-end-override"
              className="px-3 py-2 text-sm font-mono-num focus:outline-none"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--fg)", borderRadius: "8px" }}
            />
            <div className="text-[11px] mt-1" style={{ color: "var(--fg-3)" }}>
              Leave blank to use the auto-detected duration. Set this if trades near the end of your session are missing.
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2" style={{ color: "var(--fg-3)" }}>BUFFER · {bufferSeconds}s BEFORE & AFTER EACH TRADE</label>
            <input
              type="range" min="3" max="30" value={bufferSeconds}
              onChange={(e) => setBufferSeconds(Number(e.target.value))}
              data-testid="buffer-slider"
              className="w-full accent-[color:var(--accent)]"
              style={{ accentColor: "var(--accent)" }}
            />
            <div className="flex justify-between text-[10px] font-mono-num mt-1" style={{ color: "var(--fg-3)" }}>
              <span>3s</span><span>10s</span><span>20s</span><span>30s</span>
            </div>
          </div>
        </div>
      )}

      {/* Trades preview — shows ALL trades for the target date. Ones outside the
          recording window are grayed out; user can click them to force-include
          (useful when Chrome mis-detects video duration). */}
      {targetDate && (
        <div className="mt-6 p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>TRADES · {targetDate}</div>
              <div className="mt-1 text-sm" style={{ color: "var(--fg-2)" }}>
                {tradesOnDay.length === 0
                  ? "No trades logged on this day"
                  : `${tradesToCut.length} of ${tradesOnDay.length} trades selected for cutting`}
              </div>
            </div>
            {tradesToCut.length > 0 && (
              <div className="text-xs font-mono-num" style={{ color: "var(--fg-3)" }}>
                Est. {Math.round(tradesToCut.length * (bufferSeconds * 2 + 60) / 60)}m of clips
              </div>
            )}
          </div>
          {tradesOnDay.length > 0 && (
            <div className="max-h-64 overflow-y-auto space-y-1">
              {tradesOnDay.map((t) => {
                const isOutsideWindow = !t.__inWindow;
                const isExcluded = excludedTradeIds.has(t.id);
                const willCut = t.__inWindow && !isExcluded;
                return (
                  <button
                    type="button"
                    key={t.id}
                    data-testid={`trade-preview-${t.id}`}
                    onClick={() => {
                      // Clicking toggles include/exclude. For out-of-window trades,
                      // clicking flips them into the "cut anyway" state (they get
                      // moved into tradesToCut by removing from excludedTradeIds
                      // — but they're not there by default because we filter on
                      // __inWindow, so we'd need to also allow force-include).
                      // Simpler: clicking always toggles exclusion status.
                      setExcludedTradeIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                        return next;
                      });
                    }}
                    className="w-full flex items-center gap-3 text-xs py-1.5 px-2 rounded transition-colors text-left"
                    style={{
                      opacity: willCut ? 1 : 0.45,
                      background: willCut ? "transparent" : "rgba(255,255,255,0.02)",
                      cursor: "pointer",
                    }}
                    title={
                      isOutsideWindow
                        ? "Outside detected recording window — set OVERRIDE END TIME to include"
                        : isExcluded ? "Click to include" : "Click to exclude"
                    }
                  >
                    <span className="font-mono-num" style={{ color: "var(--fg-3)", minWidth: 60 }}>{new Date(t.__et).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: userTz })}</span>
                    <span className="font-semibold" style={{ color: "var(--fg)", minWidth: 60 }}>{t.symbol}</span>
                    <span style={{ color: "var(--fg-3)", minWidth: 60 }}>{t.side?.toUpperCase()}</span>
                    <span className="font-mono-num flex-1" style={{ color: t.pnl >= 0 ? "var(--green)" : "var(--loss)" }}>{fmtUSD(t.pnl)}</span>
                    {isOutsideWindow && (
                      <span className="text-[10px] tracking-wider font-mono-num px-1.5 py-0.5" style={{ background: "rgba(255,152,0,0.12)", color: "#ff9800", borderRadius: 4 }}>
                        OUTSIDE WINDOW
                      </span>
                    )}
                    {t.__inWindow && isExcluded && (
                      <span className="text-[10px] tracking-wider font-mono-num" style={{ color: "var(--fg-3)" }}>
                        EXCLUDED
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* CTA */}
      {file && tradesToCut.length > 0 && (
        <button
          onClick={process}
          disabled={processing || !!quota?.over_hard}
          data-testid="process-clips-btn"
          className="w-full mt-6 py-4 text-sm font-semibold tracking-wide flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: "var(--accent)", color: "white", borderRadius: "10px", boxShadow: "0 4px 24px rgba(168,85,247,0.35)" }}
        >
          {processing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {progress.label} ({progress.done}/{progress.total})
            </>
          ) : (
            <><Zap className="h-4 w-4" /> CUT {tradesToCut.length} CLIPS</>
          )}
        </button>
      )}

      {/* FFmpeg progress log — only visible during processing */}
      {processing && ffmpegProgress && (
        <div className="mt-3 text-[10px] font-mono-num" style={{ color: "var(--fg-3)" }}>
          {ffmpegProgress.slice(0, 200)}
        </div>
      )}

      {/* Storage cap warning */}
      {quota?.over_hard && (
        <div className="mt-4 p-3 flex items-start gap-2 text-xs" style={{ background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.3)", borderRadius: "8px", color: "var(--loss)" }}>
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>Storage limit reached ({formatBytes(quota.used_bytes)} / {formatBytes(quota.hard_limit_bytes)}). Star fewer clips or delete some to make room.</div>
        </div>
      )}

      {/* OBS Setup Guide Modal */}
      {showSetupGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.75)" }} onClick={() => setShowSetupGuide(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8"
            style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "16px" }}
            data-testid="obs-setup-modal"
          >
            <div className="label-eyebrow mb-2" style={{ color: "var(--accent)" }}>OBS STUDIO SETUP</div>
            <h2 className="font-heading text-2xl font-semibold mb-4">Optimal settings for TradeGreen Replay</h2>
            <div className="space-y-5 text-sm" style={{ color: "var(--fg-2)" }}>
              <div>
                <div className="font-semibold mb-1" style={{ color: "var(--fg)" }}>1. Output format & file name</div>
                <ul className="ml-5 space-y-1 list-disc" style={{ color: "var(--fg-2)" }}>
                  <li><strong>Settings → Output → Recording Format:</strong> MP4 (or MKV → auto-remux to MP4)</li>
                  <li><strong>File name template:</strong> <code style={{ background: "var(--surface2)", padding: "1px 6px", borderRadius: 4 }}>%CCYY-%MM-%DD %hh-%mm-%ss</code> — TradeGreen reads the exact start time from this filename automatically</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold mb-1" style={{ color: "var(--fg)" }}>2. Video quality (recommended)</div>
                <ul className="ml-5 space-y-1 list-disc">
                  <li><strong>Base &amp; output resolution:</strong> match your monitor (1920×1080 or 2560×1440)</li>
                  <li><strong>FPS:</strong> 30 (60 only if you want ultra-smooth ticks; doubles the file size)</li>
                  <li><strong>Encoder:</strong> NVENC H.264 (Nvidia) or x264 CPU preset <em>veryfast</em></li>
                  <li><strong>Bitrate:</strong> 6000–8000 Kbps CBR — good quality, ~3–4 GB per hour</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold mb-1" style={{ color: "var(--fg)" }}>3. Audio (optional but recommended)</div>
                <ul className="ml-5 space-y-1 list-disc">
                  <li>Record your desktop audio + mic so you can hear your own reactions on the replay — pure gold for reviewing revenge trades and tilt.</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold mb-1" style={{ color: "var(--fg)" }}>4. Time sync (critical!)</div>
                <ul className="ml-5 space-y-1 list-disc">
                  <li>Make sure your computer&apos;s clock is synced (Windows: <code>w32tm /resync</code>; Mac: automatic). Even a 30-second drift can misalign every clip.</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold mb-1" style={{ color: "var(--fg)" }}>5. Workflow</div>
                <ol className="ml-5 space-y-1 list-decimal">
                  <li>Start OBS recording <em>before</em> market open</li>
                  <li>Trade your session normally — log fills into TradeGreen or import CSV after</li>
                  <li>Stop OBS recording after close</li>
                  <li>Drop the .mp4 into TradeGreen Replay — we&apos;ll cut every trade automatically</li>
                </ol>
              </div>
            </div>
            <button
              onClick={() => setShowSetupGuide(false)}
              className="mt-6 w-full py-3 text-sm font-semibold"
              style={{ background: "var(--accent)", color: "white", borderRadius: "10px" }}
            >
              GOT IT
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}


// Common IANA zones for the US-focused user base. Not exhaustive — the input
// is a free-text field too, so any IANA zone works.
const COMMON_ZONES = [
  { value: "America/New_York", label: "Eastern (New York) — market time" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Australia/Sydney", label: "Sydney" },
];

function TimezoneCard({ currentTz, currentBrokerTz }) {
  const { refreshUser } = useAuth();
  const detectedTz = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "";
  const [tz, setTz] = useState(currentTz || detectedTz);
  const [brokerTz, setBrokerTz] = useState(currentBrokerTz || "America/New_York");
  const [saving, setSaving] = useState(false);
  // Auto-collapse (hide) the timezone card when the user has already set
  // their location. First-time users still see the required prompt.
  // Users can still update their timezone from the Settings page.
  const [dismissed, setDismissed] = useState(!!currentTz);

  useEffect(() => {
    setTz(currentTz || detectedTz);
    setBrokerTz(currentBrokerTz || "America/New_York");
  }, [currentTz, currentBrokerTz, detectedTz]);

  const needsSetup = !currentTz;
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/auth/timezone", { timezone: tz, broker_timezone: brokerTz });
      toast.success("Timezone saved — trade clips will now line up correctly");
      if (refreshUser) await refreshUser();
      setDismissed(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save timezone");
    } finally {
      setSaving(false);
    }
  };

  if (dismissed && !needsSetup) return null;

  return (
    <div
      className="mb-6 p-5"
      style={{
        background: needsSetup ? "rgba(168,85,247,0.06)" : "var(--surface)",
        border: `1px ${needsSetup ? "dashed" : "solid"} ${needsSetup ? "rgba(168,85,247,0.35)" : "var(--border)"}`,
        borderRadius: "12px",
      }}
      data-testid="timezone-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow flex items-center gap-1.5" style={{ color: needsSetup ? "var(--accent)" : "var(--fg-3)" }}>
          <Info className="h-3 w-3" strokeWidth={1.5} />
          {needsSetup ? "SET YOUR TIMEZONES · REQUIRED FOR ACCURATE CLIPS" : "TIMEZONE ALIGNMENT"}
        </div>
        {!needsSetup && (
          <button
            onClick={() => setDismissed(true)}
            className="text-[10px] tracking-wider"
            style={{ color: "var(--fg-3)" }}
            data-testid="timezone-collapse"
          >HIDE</button>
        )}
      </div>
      <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--fg-2)" }}>
        Your broker exports trade times in <strong style={{ color: "var(--fg)" }}>market time</strong> (usually ET),
        while your OBS recording is in <strong style={{ color: "var(--fg)" }}>your local time</strong>. We use both to
        line clips up to the exact frame. Set once — we&apos;ll remember.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-eyebrow block mb-2" style={{ color: "var(--fg-3)" }}>YOUR LOCAL TIMEZONE</label>
          <select
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            data-testid="timezone-local-select"
            className="w-full px-3 py-2 text-sm font-mono-num focus:outline-none"
            style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--fg)", borderRadius: "8px" }}
          >
            {COMMON_ZONES.find((z) => z.value === tz) ? null : <option value={tz}>{tz} (detected)</option>}
            {COMMON_ZONES.map((z) => (
              <option key={z.value} value={z.value}>{z.label}</option>
            ))}
          </select>
          <div className="text-[10px] mt-1" style={{ color: "var(--fg-3)" }}>Detected from browser: {detectedTz}</div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2" style={{ color: "var(--fg-3)" }}>BROKER EXPORT TIMEZONE</label>
          <select
            value={brokerTz}
            onChange={(e) => setBrokerTz(e.target.value)}
            data-testid="timezone-broker-select"
            className="w-full px-3 py-2 text-sm font-mono-num focus:outline-none"
            style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--fg)", borderRadius: "8px" }}
          >
            {COMMON_ZONES.map((z) => (
              <option key={z.value} value={z.value}>{z.label}</option>
            ))}
          </select>
          <div className="text-[10px] mt-1" style={{ color: "var(--fg-3)" }}>Most US brokers (IBKR, Schwab, TDA, Webull) export in ET.</div>
        </div>
      </div>
      <button
        type="button"
        onClick={save}
        disabled={saving || !tz}
        data-testid="timezone-save"
        className="mt-4 px-4 py-2 text-xs font-semibold tracking-wide transition-colors disabled:opacity-50"
        style={{ background: "var(--accent)", color: "white", borderRadius: "8px", boxShadow: needsSetup ? "var(--accent-glow)" : "none" }}
      >
        {saving ? "Saving…" : (needsSetup ? "SAVE & CONTINUE" : "UPDATE TIMEZONES")}
      </button>
    </div>
  );
}
